import * as React from 'react'

import { formatPhoneInput, phoneInputValue } from '#/shared/lib/format'
import { Combobox } from '#/shared/ui/combobox'
import type { ComboboxOption, ComboboxAction } from '#/shared/ui/combobox'
import type { CustomerListItem } from '#/shared/api/generated/shipment/models'

import { useComboboxFreeze } from '../model/use-combobox-freeze'
import { useCustomerSearch } from '../model/use-customer-search'

interface CustomerSearchComboboxProps {
  value: string
  onValueChange: (value: string) => void
  onCustomerSelect?: (customer: CustomerListItem) => void
  getTag?: (customer: CustomerListItem) => string | undefined
  getLabel?: (customer: CustomerListItem) => string
  phone?: boolean
  label?: string
  placeholder?: string
  error?: boolean
  success?: boolean
  className?: string
  suffix?: React.ReactNode
  action?: ComboboxAction
  searchDisabled?: boolean
  onClear?: () => void
}

const ALWAYS_PASS_FILTER = () => true

const EMPTY_LABEL = '—'

function customerName(customer: CustomerListItem): string {
  return [customer.first_name, customer.last_name].filter(Boolean).join(' ')
}

function customerLabel(customer: CustomerListItem): string {
  const name = customerName(customer)
  if (name.length > 0) return name
  if (customer.company && customer.company.length > 0) return customer.company
  return EMPTY_LABEL
}

function defaultTag(customer: CustomerListItem): string | undefined {
  if (customer.company && customer.company.length > 0) return customer.company
  return undefined
}

function buildOptions(
  items: CustomerListItem[],
  searchDisabled: boolean | undefined,
  getTag: (customer: CustomerListItem) => string | undefined,
  getLabel: (customer: CustomerListItem) => string,
): ComboboxOption[] {
  if (searchDisabled) return []
  return items.map((customer) => ({
    value: customer.id,
    label: getLabel(customer),
    tag: getTag(customer),
  }))
}

function searchComboboxFlags(
  searchDisabled: boolean,
  live: { isLoading: boolean; needsMoreInput: boolean },
  isUserTyped: boolean,
  action: ComboboxAction | undefined,
): {
  loading: boolean
  hideEmpty: boolean
  action: ComboboxAction | undefined
} {
  return {
    loading: !searchDisabled && live.isLoading,
    hideEmpty: searchDisabled || live.needsMoreInput || !isUserTyped,
    action: searchDisabled ? undefined : action,
  }
}

export function CustomerSearchCombobox({
  value,
  onValueChange,
  onCustomerSelect,
  getTag = defaultTag,
  getLabel = customerLabel,
  phone,
  label,
  placeholder,
  error,
  success,
  className,
  suffix,
  action,
  searchDisabled = false,
  onClear,
}: CustomerSearchComboboxProps) {
  const freeze = useComboboxFreeze<CustomerListItem>(value)
  const searchEnabled =
    freeze.isUserTyped && !searchDisabled && !freeze.isJustSelected
  const live = useCustomerSearch(value, { enabled: searchEnabled })
  const items = freeze.pickItems(live.items)
  const options = buildOptions(items, searchDisabled, getTag, getLabel)

  const handleValueChange = (next: string) => {
    const stored = phone ? phoneInputValue(next) : next
    freeze.onTyped(stored)
    onValueChange(stored)
  }

  const handleSelect = (option: ComboboxOption) => {
    freeze.freeze(option.label, items)
    const customer = items.find((it) => it.id === option.value)
    if (customer) onCustomerSelect?.(customer)
  }

  const displayValue = phone ? formatPhoneInput(value) : value
  const flags = searchComboboxFlags(
    searchDisabled,
    live,
    freeze.isUserTyped,
    action,
  )

  return (
    <Combobox
      labelSize="sm"
      label={label}
      placeholder={placeholder}
      options={options}
      value={displayValue}
      onValueChange={handleValueChange}
      onSelect={handleSelect}
      onClear={onClear}
      filter={ALWAYS_PASS_FILTER}
      loading={flags.loading}
      hideEmpty={flags.hideEmpty}
      hideToggle
      error={error}
      success={success}
      className={className}
      suffix={suffix}
      action={flags.action}
      clearable
    />
  )
}
